import { useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";

import { useInteractionInTrainingApi } from "@/v2/entities/interaction-in-training/InteractionInTraining.api";
import {
  InteractionType,
  InteractionInTrainingStatus,
  InteractionInTrainingItem,
} from "@/v2/entities/interaction-in-training/InteractionInTraining.model";
import { ExploreFiltersValue } from "@/v2/features/explore-filter-menu/ExploreFilterMenu";
import {
  ExploreInteractionRow,
  ExploreInteractionType,
} from "@/v2/entities/explore/Explore.model";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import { Topic } from "@/v2/entities/topic/Topic.model";

import { useSimulator } from "../../context/SimulatorWrapper";

import { TrainingPanelInteractionCard } from "./TrainingPanelInteractionCard";
import { InteractionListView, InteractionListItems } from "./shared";

interface PendingReviewViewProps {
  selectedTopic: Topic | null;
  topicIds?: number[];
  onNavigateToTranscript: (
    interactionId: string,
    fetchTranscriptId: string,
    interactionType: InteractionType,
    pendingReviewId?: number
  ) => void;
  onResetNavigation: () => void;
  activeFilters: ExploreFiltersValue;
  setActiveFilters: (filters: ExploreFiltersValue) => void;
  sharedFilter?: Filter;
  selectedInteractionType: InteractionType;
  onInteractionTypeChange: (type: InteractionType) => void;
  search?: string;
  onSearchChanged?: (search: string) => void;
}

export const PendingReviewView = ({
  selectedTopic,
  onNavigateToTranscript,
  activeFilters,
  setActiveFilters,
  sharedFilter,
  selectedInteractionType,
  onInteractionTypeChange,
  search = "",
  onSearchChanged,
}: PendingReviewViewProps) => {
  const interactionApi = useInteractionInTrainingApi();
  const queryClient = useQueryClient();

  const { topics } = useSimulator();

  const effectiveInteractionType = useMemo(() => {
    return selectedInteractionType === InteractionType.TICKET
      ? InteractionType.TICKET
      : InteractionType.CONVERSATION;
  }, [selectedInteractionType]);

  const topicsId: number[] = useMemo(() => {
    if (!activeFilters.topic) return [];

    const topicFilter = activeFilters.topic;

    if (topicFilter.operator === "in" && Array.isArray(topicFilter.value)) {
      return (
        topics
          ?.filter((topic) => topicFilter.value.includes(topic.topic))
          .map((topic) => topic.id) || []
      );
    }

    if (topicFilter.operator === "eq") {
      return (
        topics
          ?.filter((topic) => topic.topic === topicFilter.value)
          .map((topic) => topic.id) || []
      );
    }

    return [];
  }, [activeFilters, topics]);

  const pendingReviewQuery = useQuery({
    queryKey: [
      "pending-review",
      selectedTopic,
      topicsId,
      effectiveInteractionType,
    ],
    queryFn: () =>
      interactionApi.getInteractionsInTraining({
        topicIds: topicsId,
        statuses: [InteractionInTrainingStatus.PENDING],
        interactionTypes: [effectiveInteractionType],
      }),
    enabled: true,
  });

  const filteredInteractions = useMemo(() => {
    if (!pendingReviewQuery.data?.interactions) return [];

    if (!search) return pendingReviewQuery.data.interactions;

    return pendingReviewQuery.data.interactions.filter((interaction) => {
      const searchLower = search.toLowerCase();
      const firstUserComment =
        interaction.firstUserComment?.toLowerCase() || "";
      const displayId = interaction.displayId?.toLowerCase() || "";
      const ticketId = interaction.ticketId?.toLowerCase() || "";

      return (
        firstUserComment.includes(searchLower) ||
        displayId.includes(searchLower) ||
        ticketId.includes(searchLower)
      );
    });
  }, [pendingReviewQuery.data?.interactions, search]);

  const isLoadingPendingReview = pendingReviewQuery.isLoading;

  const removeFromPendingMutation = useMutation({
    mutationFn: async (interaction: InteractionInTrainingItem) => {
      return interactionApi.changeStatus({
        interactionInTrainingId: interaction.id,
        interactionType: interaction.interactionType,
        status: InteractionInTrainingStatus.IGNORED,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-review"] });
      toast.success("Removed from review");
    },
  });

  const handleRemove = useCallback(
    (interaction: InteractionInTrainingItem, e: React.MouseEvent) => {
      e.stopPropagation();
      removeFromPendingMutation.mutate(interaction);
    },
    [removeFromPendingMutation]
  );

  const convertToExploreInteractionRow = useCallback(
    (interaction: InteractionInTrainingItem): ExploreInteractionRow => {
      const toISOString = (date: any): string => {
        if (date instanceof Date) {
          return date.toISOString();
        }
        if (typeof date === "string") {
          return date;
        }
        if (typeof date === "number") {
          return new Date(date).toISOString();
        }
        return new Date().toISOString();
      };

      return {
        interactionType:
          effectiveInteractionType === InteractionType.TICKET
            ? "tickets"
            : "conversations",
        id:
          interaction.ticketId ||
          interaction.conversationId ||
          interaction.id.toString(),
        displayId: interaction.displayId || interaction.id.toString(),
        subject: interaction.firstUserComment || "No content available",
        description: interaction.firstUserComment || "No content available",
        createdAt: toISOString(interaction.createdAt),
        updatedAt: toISOString(interaction.updatedAt),
        status: interaction.status,
        channel:
          effectiveInteractionType === InteractionType.TICKET
            ? "ticket"
            : "conversation",
        topic: selectedTopic?.topic || "",
        category: "",
        countPublicMessages: 0,
        countInternalMessages: 0,
      };
    },
    [selectedInteractionType, selectedTopic, effectiveInteractionType]
  );

  const handleChooseInteraction = useCallback(
    (interaction: InteractionInTrainingItem) => {
      if (
        interaction.interactionType === InteractionType.TICKET &&
        interaction.ticketId &&
        interaction.displayId
      ) {
        onNavigateToTranscript(
          interaction.ticketId,
          interaction.displayId,
          interaction.interactionType,
          interaction.id
        );
      } else if (
        interaction.interactionType === InteractionType.CONVERSATION &&
        interaction.displayId &&
        interaction.conversationId
      ) {
        onNavigateToTranscript(
          interaction.conversationId,
          interaction.displayId,
          interaction.interactionType,
          interaction.id
        );
      }
    },
    [onNavigateToTranscript]
  );

  const handleTabChange = useCallback(
    (value: string) => {
      onInteractionTypeChange(
        value === "tickets"
          ? InteractionType.TICKET
          : InteractionType.CONVERSATION
      );
    },
    [onInteractionTypeChange]
  );

  const getExploreInteractionType = useCallback((): ExploreInteractionType => {
    return effectiveInteractionType === InteractionType.TICKET
      ? "tickets"
      : "conversations";
  }, [effectiveInteractionType]);

  return (
    <InteractionListView
      showTabs
      selectedTabValue={
        effectiveInteractionType === InteractionType.TICKET
          ? "tickets"
          : "conversations"
      }
      onTabChange={handleTabChange}
      activeFilters={activeFilters}
      setActiveFilters={setActiveFilters}
      filterSource={
        effectiveInteractionType === InteractionType.CONVERSATION
          ? "conversations"
          : "tickets"
      }
      allowedFilterFields={["topic", "source"]}
      filter={sharedFilter}
      isLoading={isLoadingPendingReview}
      isEmpty={filteredInteractions.length === 0}
      loadingText="Loading pending review..."
      emptyText="No pending review items"
      search={search}
      onSearchChanged={onSearchChanged}
      showSearchAndFilters={true}
    >
      <InteractionListItems gap="2">
        {filteredInteractions.map((interaction) => {
          const exploreInteraction =
            convertToExploreInteractionRow(interaction);

          return (
            <TrainingPanelInteractionCard
              key={interaction.id}
              interaction={exploreInteraction}
              interactionType={getExploreInteractionType()}
              onClick={() => handleChooseInteraction(interaction)}
              trainingReason={interaction.reason}
              submitter={interaction.submitter}
              isPendingReview={true}
              onRemove={(e) => handleRemove(interaction, e)}
            />
          );
        })}
      </InteractionListItems>
    </InteractionListView>
  );
};
